import IconComponent from "@CubeComponents/icons";
import { CONSTANTS } from "./globalVariable";
import { SignTypeValues } from ".";
import { t } from "@i18n";
import { resolveFileIcon, resolveResourceIcon } from "@CubeComponents/FileManager/fileIconResolvers";

export const getTextboxStyle = (isDisabled: boolean) => {
  return {
    color: "var(--primary-text)",
    height: "2rem",
    ...(isDisabled && {
      background: "var(--button-light)",
      border: "var(--none) !important",
    }),
  };
};

export const getTextboxParentStyle = (widthInPercentage: string, margin?: string) => {
  return {
    borderRadius: "0.125rem",
    width: widthInPercentage,
    border: "var(--none)",
    margin: `${margin} !important`,
  };
};

export const getTextboxClassStyle = () => {
  return {
    outline: CONSTANTS.NONE,
    borderRadius: "0.125rem !important",
    borderColor: "var(--text-light) !important",
    "&:not(.e-disabled):not(.e-input-focus):hover": {
      borderColor: "var(--primary-text) !important",
    },
    "&.e-disabled:hover.e-disabled": {
      borderColor: "var(--transparent)",
    },
    "&.e-input-focus": {
      borderColor: "var(--inner-border) !important",
    },
    "&.e-input-group": {
      border: "1px solid var(--primary-blue) !important",
    },
    "& .e-spin-down,& .e-spin-up,& .e-input-group-icon": {
      background: "var(--transparent) !important",
      color: "var(--primary-text) !important",
    },
    "& .e-spin-down:hover,& .e-spin-up:hover, & .e-input-group-icon:hover": {
      background: "var(--button-light) !important",
      color: "var(--center-border) !important",
    },
    "& .e-spin-down:active,& .e-spin-up:active, & .e-input-group-icon:active": {
      background: "var(--lighter-gray) !important",
      color: "var(--center-border) !important",
    },
    "& textarea": {
      resize: "none !important",
    },
    "&.e-disabled": {
      borderRadius: "0.125rem !important",
      "& .e-input": {
        borderRadius: "0.125rem !important",
      },
      border: "var(--none) !important",
    },
    "& .e-input[disabled]": {
      WebkitTextFillColor: "var(--disabled-text-gray) !important",
    },
  };
};

export const getSizeClass = (size: string | undefined) => {
  switch (size) {
    case "XXS":
      return "e-avatar-xxsmall";
    case "LSM":
      return "e-avatar-little-small";
    case "XS":
      return "e-avatar-xsmall";
    case "SM":
      return "e-avatar-small";
    case "LG":
      return "e-avatar-large";
    case "LLG":
      return "e-avatar-little-large";
    case "XL":
      return "e-avatar-xlarge";
    default:
      return "e-avatar-small";
  }
};

export const getAvatarStyle = (bgColor: string | undefined) => {
  return {
    "& img": {
      height: "106%",
    },
    "&.e-avatar-square-organization": {
      boxShadow: "0 0 0.125rem 0.03125rem var(--box-shadow)",
    },
    "& span": {
      fontWeight: "var(--font-weight-600)",
    },
    "&.e-avatar-xxsmall": {
      width: "1.25rem",
      height: "1.25rem",
      "& span": {
        fontSize: "0.5625rem",
      },
    },
    "&.e-avatar-xsmall": {
      width: "1.5rem",
      height: "1.5rem",
      "&.e-avatar-square-portal": {
        borderRadius: "0.25rem",
      },
      "&.e-avatar-square-organization": {
        borderRadius: "0.1875rem",
      },
      "& span": {
        fontSize: "0.75rem",
      },
    },
    "&.e-avatar-little-small": {
      width: "1.875rem",
      height: "1.875rem",
      "& span": {
        fontSize: "1rem",
      },
    },
    "&.e-avatar-small": {
      width: "2.25rem",
      height: "2.25rem",
      "&.e-avatar-square-portal": {
        borderRadius: "0.375rem",
      },
      "&.e-avatar-square-organization": {
        borderRadius: "0.0625rem",
      },
      "& span": {
        fontSize: "1.25rem",
      },
    },
    "&.e-avatar-large": {
      width: "3rem",
      height: "3rem",
      "&.e-avatar-square-portal": {
        borderRadius: "0.5rem",
      },
      "&.e-avatar-square-organization": {
        borderRadius: "0.125rem",
      },
      "& span": {
        fontSize: "1.625rem",
      },
    },
    "&.e-avatar-little-large": {
      width: "4.6875rem",
      height: "4.6875rem",
      "&.e-avatar-square-portal": {
        borderRadius: "0.75rem",
      },
      "&.e-avatar-square-organization": {
        borderRadius: "0.1875rem",
      },
      "& span": {
        fontSize: "2rem",
      },
    },
    "&.e-avatar-xlarge": {
      width: "6.25rem",
      height: "6.25rem",
      "&.e-avatar-square-portal": {
        borderRadius: "1rem",
      },
      "&.e-avatar-square-organization": {
        borderRadius: "0.375rem",
      },
      "& span": {
        fontSize: "3.5rem",
      },
    },
    backgroundColor: `${bgColor ? bgColor : "var(system-colour-9)"} !important`,
    "&.e-avatar": {
      fontFamily: "Source Sans 3 !important",
    },
  };
};

export const getAvatarGroupStyle = () => {
  return {
    display: "var(--display-flex)",
    gap: "0.1875rem",
    justifyContent: "flex-start",
    "& .e-avatar:not(:first-child)": {
      marginInlineStart: "-0.75rem",
    },
  };
};

export const getCardStyle = (width: string | number, background: string | undefined, textColor: string | undefined) => {
  return {
    width,
    background: "linear-gradient(111deg, var(--surface-white) 45%, var(--white-opacity) 129%)",
    boxShadow: "0.25rem 0.25rem 0.9375rem 0 var(--card-box-shadow)",
    borderRadius: "0.5rem",
    backgroundImage: "linear-gradient(132deg, #fff, rgba(255, 255, 255, 0.5) 100%)",
    "& > .e-card-header": {
      background: "rgba(209, 223, 244, 0.5)",
      padding: "0 !important",
      width: "100% !important",
      "& .e-card-header-content-wrapper": {
        display: "var(--display-flex)",
        justifyContent: "var(--justify-space-between)",
        padding: "0.6875rem 1.25rem 0.6875rem 1.25rem",
        "& .e-card-header-title": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontStyle: "var(--font-style-normal)",
          fontWeight: "var(--font-weight-bold)",
          fontSize: "0.875rem",
          lineHeight: "1.125rem",
          color: textColor,
          "& .e-card-header-sub-title": {
            color: "var(--soft-gray)",
          },
        },
        "& .e-card-header-link": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontStyle: "var(--font-style-normal)",
          fontWeight: "var(--font-weight-700)",
          fontSize: "0.875rem",
          lineHeight: "1.125rem",
          background: `linear-gradient(100.44deg, var(--primary-gradient-color1) 0%, var(--primary-gradient-color2) 110.4%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "var(--transparent)",
          backgroundClip: "text",
          paddingInlineEnd: "0.625rem",
        },
        "& .e-card-header-share": {
          marginInlineStart: "1rem",
          display: "flex",
          alignSelf: "end",
        },
        "& .e-card-header-max": {
          marginInlineEnd: "1.125rem",
        },
        "& .e-card-header-ellipcise": {
          padding: "0",
          background: "white",
          border: "none",
          display: "flex",
        },
      },
    },
    "&.e-card, &.e-card:hover": {
      border: "var(--none) !important",
      padding: "0",
      flexBasis: "19.375rem",
      flexShrink: "1",
    },

    "& > .e-card-content": {
      display: "flex",
      padding: "0",
      height: "100%",
      alignItems: "flex-start",
      background: background,
      color: textColor,
      overflow: "auto",
    },
  };
};

export const getSliderParentWrapperStyle = () => {
  return {
    "& .e-slider-hover .e-slider-track": {
      background: "var(--soft-gray) !important",
    },
    "& .e-control-wrapper.e-slider-container.e-horizontal": {
      display: "flex",
    },
  };
};

// Every one of these is optional on `SliderComponentProps`; `SliderComponent` fills the four gradient
// colours from `defaultSliderComponentProps`, `color` and `handleShadowColor` have no call site today.
export const getSliderStyle = (
  gradientColor1: string | undefined,
  gradientColor2: string | undefined,
  color: string | undefined,
  handleShadowColor: string | undefined,
  handleGradientColor1: string | undefined,
  handleGradientColor2: string | undefined,
) => {
  return {
    "&.e-slider": {
      "& .e-range": {
        background:
          gradientColor1 && gradientColor2
            ? `linear-gradient(100.44deg, ${gradientColor1}  0%, ${gradientColor2} 110.4%) !important`
            : color,
      },
      "& .e-handle": {
        border: "var(--none) !important",
        boxShadow: handleShadowColor
          ? `${handleShadowColor}45 0 0 0 0.25rem`
          : "var(--primary-gradient-color2-opacity) 0 0 0 0.25rem",
        transition: "transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) 0s",
        // Fall back to the same values `defaultSliderComponentProps` supplies rather than
        // interpolating the literal text "undefined" into the gradient.
        background: `linear-gradient(100.44deg, ${handleGradientColor1 ?? "var(--primary-gradient-color1)"}  0%, ${
          handleGradientColor2 ?? "var(--primary-gradient-color2)"
        } 110.4%) !important`,
      },
      "& .e-slider-track": {
        borderRadius: "0.625rem",
        "&:hover": {
          background: "var(--soft-gray) !important",
        },
      },
    },
  };
};

export const getSignStatusInitialUserInformation = (
  fileVersionId: string,
  resourceType: string,
  userDetails: { firstName?: string; lastName?: string; id?: string } | undefined,
  projectData: { id?: string } | undefined,
) => {
  return {
    name: `${userDetails?.firstName} ${userDetails?.lastName}`,
    userId: userDetails?.id,
    projectId: projectData?.id,
    signType: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
    signDoc: "",
    comment: "",
    fileVersionId: fileVersionId,
    resourceType: resourceType,
    prevStatus: "",
    status: "",
    error: {
      signDoc: "",
      comment: "",
      status: "",
    },
  };
};

export const getNameInitials = (
  // Both members carry `| undefined`: every caller reads them off a fetched profile whose own
  // `firstName`/`lastName` are optional, so an explicit `undefined` is the normal case here, and the
  // optional-chained reads below already treat it exactly like an absent key.
  userData: { firstName?: string | undefined; lastName?: string | undefined } | undefined,
) => {
  // Both members are optional, so each initial is taken independently and an absent one contributes
  // nothing. The previous form asserted the reads to `string`: with no `firstName` it returned
  // `undefined`, and with a `lastName` present it produced the literal text "undefinedX", which the
  // avatar rendered as the user's initials.
  const first = userData?.firstName?.[0]?.toUpperCase() ?? "";
  const last = userData?.lastName?.[0]?.toUpperCase() ?? "";
  return `${first}${last}`;
};

export { isMergeAllowed } from "@Helpers/constants";
export { getUrlForImage } from "../constants";

export const conversionStatusTemplate = (params: { conversionStatus: string }) => {
  let name;
  let tooltip;

  if (params.conversionStatus == "NOT_STARTED" || params.conversionStatus == "RETRY") {
    name = "FileConversionStatusNotStarted";
    if (params.conversionStatus == "NOT_STARTED") {
      tooltip = t("Not Started");
    } else {
      tooltip = t("Retry");
    }
  } else if (params.conversionStatus == "IN_PROGRESS") {
    name = "FileConversionStatusInProgress";
    tooltip = t("In Progress");
  } else if (params.conversionStatus == "COMPLETED") {
    name = "FileConversionStatusSuccessfull";
    tooltip = t("Successful");
  } else if (params.conversionStatus == "ERRORED") {
    name = "FileConversionStatusUnsuccessfull";
    tooltip = t("Failed");
  } else if (params.conversionStatus == "NOT_REQUIRED") {
    name = "FileConversionStatusNotRequired";
    tooltip = t("Conversion Not Required");
  } else {
    return "";
  }
  return (
    <div style={{ width: "1rem" }}>
      <IconComponent name={name} position="bottom" toolTipContent={tooltip} width="1.5rem" height="1.5rem" />
    </div>
  );
};

const fileIcons: Record<string, unknown> = import.meta.glob("../../assets/icons/svg/File*.svg", {
  eager: true,
  import: "default",
});
const shortcutIcons: Record<string, unknown> = import.meta.glob(
  "../../assets/icons/svg/filesShortcutIcons/Sfile*.svg",
  {
    eager: true,
    import: "default",
  },
);

export const getFileIcon = (fileName: string = "", className: string = "", isShortCutFile: boolean = false) => {
  const { name, fallback } = resolveFileIcon({ fileName, isShortCutFile });
  const registry = isShortCutFile ? shortcutIcons : fileIcons;
  const path = `../../assets/icons/svg/${name}.svg`;
  const finalName = registry[path] ? name : (fallback ?? name);
  return <IconComponent className={className} name={finalName} width="1.5rem" height="1.5rem" />;
};

export const getIconbasedOnResourceType = (file: Parameters<typeof resolveResourceIcon>[0]["file"], className = "") => {
  const result = resolveResourceIcon({ file });
  if (!result) return "";
  if (result.kind === "file") {
    return getFileIcon(result.fileName, className, result.isShortCutFile);
  }
  return <IconComponent className={className} name={result.name} width="1.5rem" height="1.5rem" />;
};
